import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";

const AU_STATES = ['VIC','NSW','QLD','WA','SA','TAS','ACT','NT'];

const css = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .wl-fadein { animation: fadeUp 0.6s ease both; }
  .wl-a1 { animation-delay: 0.1s; }
  .wl-a2 { animation-delay: 0.22s; }
  .wl-a3 { animation-delay: 0.34s; }
  .wl-a4 { animation-delay: 0.46s; }
  .wl-success { animation: fadeUp 0.5s ease both; }

  .wl-input {
    width: 100%;
    background: rgba(255,255,255,0.055);
    border: 1px solid rgba(194,168,130,0.22);
    border-radius: 8px;
    padding: 12px 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.88rem;
    font-weight: 300;
    color: #f5efe6;
    outline: none;
    transition: border-color 0.2s, background 0.2s;
    box-sizing: border-box;
  }
  .wl-input::placeholder { color: rgba(196,168,130,0.45); }
  .wl-input:focus {
    border-color: #c17f4a;
    background: rgba(193,127,74,0.07);
  }
  .wl-select {
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
  }

  .wl-role-btn {
    flex: 1;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid rgba(194,168,130,0.22);
    background: rgba(255,255,255,0.03);
    color: rgba(245,239,230,0.45);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.8rem;
    letter-spacing: 0.07em;
    cursor: pointer;
    transition: all 0.18s;
  }
  .wl-role-btn.active {
    border-color: #c17f4a;
    background: rgba(193,127,74,0.13);
    color: #d4956a;
  }

  .wl-submit {
    width: 100%;
    padding: 14px;
    border-radius: 8px;
    border: none;
    background: #c17f4a;
    color: #fff;
    font-family: 'DM Mono', monospace;
    font-size: 0.7rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;
  }
  .wl-submit:hover:not(:disabled) {
    background: #a3662e;
    transform: translateY(-1px);
  }
  .wl-submit:active:not(:disabled) { transform: translateY(0); }
  .wl-submit:disabled { opacity: 0.55; cursor: not-allowed; }

  @media (max-width: 500px) {
    .wl-form-grid { grid-template-columns: 1fr !important; }
    .wl-nav { padding: 0 20px !important; }
    .wl-stats { gap: 22px !important; }
  }
`;

export default function WaitlistPage() {
  const [fname, setFname]     = useState('');
  const [lname, setLname]     = useState('');
  const [email, setEmail]     = useState('');
  const [role, setRole]       = useState('');
  const [state, setState]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState(null);

  const lbl = {
    display: 'block',
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: '#6b4f3a',
    marginBottom: 7,
  };

  const handleSubmit = async () => {
    setError(null);
    if (!fname.trim() || !lname.trim() || !email.trim() || !role || !state) {
      setError('All fields are required.'); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.'); return;
    }
    setSubmitting(true);
    const { error: err } = await supabase.from('waitlist').insert({
      first_name: fname.trim(),
      last_name:  lname.trim(),
      email:      email.trim(),
      role,
      state,
      created_at: new Date().toISOString(),
    });
    setSubmitting(false);
    if (err) { setError(err.message); return; }
    setSuccess(true);
  };

  return (
    <>
      <style>{css}</style>

      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1e1410 0%, #5c3d2e 55%, #7a5230 100%)', position: 'relative', overflow: 'hidden' }}>

        {/* warm glow */}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 55% 65% at 75% 35%, rgba(193,127,74,0.18) 0%, transparent 65%)', pointerEvents: 'none' }}/>

        {/* grain texture */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`, backgroundSize: '256px 256px', pointerEvents: 'none' }}/>

        {/* ── nav ── */}
        <nav className="wl-nav" style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', height: 58 }}>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: '1.15rem', color: '#f5efe6', letterSpacing: '-0.01em' }}>
            opencall<span style={{ color: '#c17f4a' }}>.</span>
          </span>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.18em', color: '#c4a882', border: '1px solid rgba(194,168,130,0.3)', borderRadius: 20, padding: '4px 12px' }}>
            EARLY ACCESS
          </span>
        </nav>

        {/* ── hero body ── */}
        <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 24px 0', textAlign: 'center' }}>

          {/* eyebrow */}
          <div className="wl-fadein wl-a1" style={{ display: 'inline-block', fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.2em', color: '#d4956a', border: '1px solid rgba(193,127,74,0.35)', borderRadius: 20, padding: '5px 16px', marginBottom: 28 }}>
            AUSTRALIAN ARTISTS &amp; GALLERIES
          </div>

          {/* headline */}
          <h1 className="wl-fadein wl-a2" style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(2.4rem, 6vw, 4rem)', fontWeight: 400, color: '#f5efe6', lineHeight: 1.12, margin: '0 0 22px', maxWidth: 640 }}>
            Be first through<br/>
            <em style={{ color: '#d4956a', fontStyle: 'italic' }}>the open door.</em>
          </h1>

          {/* subheadline */}
          <p className="wl-fadein wl-a3" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1.1rem', fontWeight: 300, color: 'rgba(245,239,230,0.62)', lineHeight: 1.65, maxWidth: 500, margin: '0 0 44px' }}>
            opencall. connects Australian artists and galleries through open calls —
            exhibitions, grants, and residencies. Apply directly. No middleman.
          </p>

          {/* ── waitlist card ── */}
          <div className="wl-fadein wl-a4" style={{ width: '100%', maxWidth: 500, background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(194,168,130,0.18)', borderRadius: 14, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: '36px 32px', marginBottom: 52, textAlign: 'left' }}>

            {success ? (
              /* success state */
              <div className="wl-success" style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{ width: 50, height: 50, borderRadius: '50%', border: '1px solid #c17f4a', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#d4956a', fontSize: '1.2rem' }}>✓</div>
                <div style={{ fontFamily: "'Fraunces', serif", fontSize: '1.4rem', fontWeight: 400, color: '#f5efe6', marginBottom: 12 }}>You're on the list.</div>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: 'rgba(245,239,230,0.5)', lineHeight: 1.65, margin: 0 }}>
                  We'll be in touch when opencall. opens its doors. Thanks for being early.
                </p>
              </div>
            ) : (
              <>
                {/* name row */}
                <div className="wl-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={lbl}>First name</label>
                    <input className="wl-input" value={fname} onChange={e => setFname(e.target.value)} placeholder="Mia"/>
                  </div>
                  <div>
                    <label style={lbl}>Last name</label>
                    <input className="wl-input" value={lname} onChange={e => setLname(e.target.value)} placeholder="Chen"/>
                  </div>
                </div>

                {/* email */}
                <div style={{ marginBottom: 14 }}>
                  <label style={lbl}>Email</label>
                  <input className="wl-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="mia@studio.com.au"/>
                </div>

                {/* role */}
                <div style={{ marginBottom: 14 }}>
                  <label style={lbl}>I am a…</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className={`wl-role-btn ${role === 'artist' ? 'active' : ''}`} onClick={() => setRole('artist')}>Artist</button>
                    <button className={`wl-role-btn ${role === 'gallery' ? 'active' : ''}`} onClick={() => setRole('gallery')}>Gallery</button>
                  </div>
                </div>

                {/* state */}
                <div style={{ marginBottom: 24 }}>
                  <label style={lbl}>State / Territory</label>
                  <div style={{ position: 'relative' }}>
                    <select className="wl-input wl-select" value={state} onChange={e => setState(e.target.value)}>
                      <option value="" disabled>Select…</option>
                      {AU_STATES.map(s => <option key={s} value={s} style={{ background: '#3b2f2f', color: '#f5efe6' }}>{s}</option>)}
                    </select>
                    <span style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', color: 'rgba(196,168,130,0.45)', pointerEvents: 'none', fontSize: '0.75rem' }}>▾</span>
                  </div>
                </div>

                {/* error */}
                {error && (
                  <div style={{ background: '#fde0de', border: '1px solid #c0524a', borderRadius: 6, padding: '10px 14px', fontSize: '0.82rem', color: '#c0524a', marginBottom: 16 }}>
                    ⚠ {error}
                  </div>
                )}

                {/* submit */}
                <button className="wl-submit" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Joining…' : 'Join the waitlist →'}
                </button>

                <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'rgba(140,111,90,0.55)', textAlign: 'center', marginTop: 14, marginBottom: 0 }}>
                  No spam. Just your spot in line.
                </p>
              </>
            )}
          </div>
        </div>

        {/* ── stats bar ── */}
        <div style={{ position: 'relative', zIndex: 10, borderTop: '1px solid rgba(194,168,130,0.1)', padding: '28px 24px 40px' }}>
          <div className="wl-stats" style={{ display: 'flex', justifyContent: 'center', gap: 52, flexWrap: 'wrap' }}>
            {[
              { n: '4',    l: 'Opportunity Types' },
              { n: '8',    l: 'States & Territories' },
              { n: '3',    l: 'Artist Plan Tiers' },
              { n: 'Free', l: 'To Get Started' },
            ].map(({ n, l }) => (
              <div key={l} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Fraunces', serif", fontSize: '1.5rem', fontWeight: 400, color: '#d4956a', marginBottom: 4 }}>{n}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8c6f5a' }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── returning user link ── */}
        <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', paddingBottom: 32 }}>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', color: 'rgba(140,111,90,0.45)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'rgba(140,111,90,0.6)', textDecoration: 'none', borderBottom: '1px solid rgba(140,111,90,0.3)' }}>
              Log in
            </Link>
          </span>
        </div>

      </div>
    </>
  );
}
